import axios from 'axios';
import { CreateRoomBookingRequest, RoomBooking, CreateRoomBookingResponse, RoomBookingNeedsUserActionCountResponse } from './models/RoomBooking';
import { Page } from './models/HostRoomBooking';
import { RoomBookingDetails } from './models/RoomBookingDetails';
import { UpdateProfileRequest, UserProfile } from './models/Profile';
import { GenerateUploadUrlsRequest } from './models/GenerateUploadUrlsRequest';
import { GenerateUploadUrlsResponse } from './models/GenerateUploadUrlsResponse';
import { CreateReviewRequest, UpdateReviewRequest, Review } from './models/CreateReviewRequest';
import {
  CreateSpaceReviewRequest,
  SpaceReviewResponse,
} from './models/SpaceReview';
import {
  CreateTutorReviewRequest,
  CreateTutorReviewResponse,
} from './models/CreateTutorReviewRequest';
import { TutorReviewEligibilityResponse } from './models/TutorReview';
import { DeclineChangeRequest } from './models/RoomBookingChange';
import { DeclineTutorBookingChangeRequest } from './models/TutorBookingChange';
import { RewardsSummary, VoucherRedeemRequest } from './models/Rewards';
import {
  PassCheckoutRequest,
  PassCheckoutResponse,
  PassTargetType,
  PassUsageEvent,
  PassWalletDetail,
  PassWalletSummary,
} from './models/Pass';
import {
  CreateTutorBookingRequest,
  CreateTutorBookingResponse,
  TutorBookingFilter,
  TutorBookingNeedsUserActionCountResponse,
  UserTutorBookingDetails,
  UserTutorBookingListItem,
  UserTutorBookingSummaryCountsResponse,
} from './models/Tutor';
import { applyClientChannelHeader } from './utils/clientChannel';
import { applyAllowTestOrgsHeader } from './utils/experimentalFeatures';
import { getBookingAnalyticsPayload } from './utils/analytics';

// Define the base API URL based on the environment
const baseURL = process.env.REACT_APP_API_BASE_URL + "/user";

class UserApiClient {
  public axiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem("access_token");
        const assumedUserId = localStorage.getItem("assumed_user_id");
    
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        } 

        if (assumedUserId) {
          config.headers['X-UserId'] = assumedUserId;
        }

        applyClientChannelHeader(config.headers);
        applyAllowTestOrgsHeader(config.headers);
    
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => this.handleErrorResponse(error)
    );
  }

  private handleErrorResponse(error: any): Promise<never> {
    const status = error.response ? error.response.status : null;

    // Only clear session on 401 (invalid/expired token). 403 is authorization, not auth failure.
    if (status === 401) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("phone_number");
    }

    return Promise.reject(error);
  }

  public async createRoomBooking(request: CreateRoomBookingRequest): Promise<CreateRoomBookingResponse> {
    try {
      const response = await this.axiosInstance.post<CreateRoomBookingResponse>('/room-bookings', {
        ...request,
        analytics: request.analytics ?? getBookingAnalyticsPayload(),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating room booking:', error);
      throw error;
    }
  }

  public async createTutorBooking(request: CreateTutorBookingRequest): Promise<CreateTutorBookingResponse> {
    try {
      const response = await this.axiosInstance.post<CreateTutorBookingResponse>('/tutor-bookings', {
        ...request,
        analytics: request.analytics ?? getBookingAnalyticsPayload(),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating tutor booking:', error);
      throw error;
    }
  }

  /** GET /v1/user/tutor-bookings — paginated list with optional filter tab. */
  public async getTutorBookingsPage(
    filter: TutorBookingFilter = 'all',
    page: number = 0,
    size: number = 10,
  ): Promise<Page<UserTutorBookingListItem>> {
    try {
      const response = await this.axiosInstance.get<Page<UserTutorBookingListItem>>('/tutor-bookings', {
        params: { filter, page, size },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching tutor bookings:', error);
      throw error;
    }
  }

  /** GET /v1/user/tutor-bookings/summary — tab badge counts. */
  public async getTutorBookingsSummary(): Promise<UserTutorBookingSummaryCountsResponse> {
    try {
      const response = await this.axiosInstance.get<UserTutorBookingSummaryCountsResponse>(
        '/tutor-bookings/summary',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching tutor bookings summary:', error);
      throw error;
    }
  }

  /** GET /v1/user/tutor-bookings/needs-user-action/count — actionable badge total only. */
  public async getTutorBookingsNeedsUserActionCount(): Promise<TutorBookingNeedsUserActionCountResponse> {
    try {
      const response = await this.axiosInstance.get<TutorBookingNeedsUserActionCountResponse>(
        '/tutor-bookings/needs-user-action/count',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching tutor bookings needs-user-action count:', error);
      throw error;
    }
  }

  /** GET /v1/user/tutor-bookings/{bookingId} */
  public async getTutorBookingDetails(bookingId: string): Promise<UserTutorBookingDetails> {
    try {
      const response = await this.axiosInstance.get<UserTutorBookingDetails>(`/tutor-bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tutor booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** POST /v1/user/tutor-bookings/{bookingId}/changes/{changeId}/accept */
  public async acceptTutorBookingChange(bookingId: string, changeId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/tutor-bookings/${bookingId}/changes/${changeId}/accept`);
    } catch (error) {
      console.error(`Error accepting tutor booking change ${changeId} for booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** POST /v1/user/tutor-bookings/{bookingId}/changes/{changeId}/decline */
  public async declineTutorBookingChange(
    bookingId: string,
    changeId: string,
    request: DeclineTutorBookingChangeRequest,
  ): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/tutor-bookings/${bookingId}/changes/${changeId}/decline`, request);
    } catch (error) {
      console.error(`Error declining tutor booking change ${changeId} for booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** POST /v1/user/tutor-bookings/{bookingId}/confirm — 200 OK, no body. Idempotent. */
  public async confirmTutorBookingAttendance(bookingId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/tutor-bookings/${bookingId}/confirm`);
    } catch (error) {
      console.error(`Error confirming tutor booking attendance ${bookingId}:`, error);
      throw error;
    }
  }

  /** POST /v1/user/tutor-bookings/series/{recurringSeriesId}/cancel — releases the shared hold. */
  public async cancelTutorBookingSeries(recurringSeriesId: string, reason?: string): Promise<void> {
    try {
      const trimmed = reason?.trim();
      await this.axiosInstance.post<void>(`/tutor-bookings/series/${recurringSeriesId}/cancel`, null, {
        params: trimmed ? { reason: trimmed } : {},
      });
    } catch (error) {
      console.error(`Error cancelling tutor booking series ${recurringSeriesId}:`, error);
      throw error;
    }
  }

  /** DELETE /v1/user/tutor-bookings/{bookingId}?reason=... — 204 No Content */
  public async cancelTutorBooking(bookingId: string, reason?: string): Promise<void> {
    try {
      await this.axiosInstance.delete(`/tutor-bookings/${bookingId}`, {
        params: reason != null && reason.trim() !== '' ? { reason: reason.trim() } : {},
      });
    } catch (error) {
      console.error(`Error cancelling tutor booking ${bookingId}:`, error);
      throw error;
    }
  }

  public async getRoomBookings(past: boolean, page: number = 0, size: number = 10): Promise<Page<RoomBooking>> {
    try {
      const response = await this.axiosInstance.get<Page<RoomBooking>>('/room-bookings', {
        params: {
          past,
          page,
          size,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching room bookings:', error);
      throw error;
    }
  }

  /** GET /v1/user/room-bookings/needs-user-action/count — actionable badge total only. */
  public async getRoomBookingsNeedsUserActionCount(): Promise<RoomBookingNeedsUserActionCountResponse> {
    try {
      const response = await this.axiosInstance.get<RoomBookingNeedsUserActionCountResponse>(
        '/room-bookings/needs-user-action/count',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching room bookings needs-user-action count:', error);
      throw error;
    }
  }

  public async getRoomBookingDetails(bookingId: string): Promise<RoomBookingDetails> {
    try {
      const response = await this.axiosInstance.get<RoomBookingDetails>(`/room-bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching room booking details for booking ${bookingId}:`, error);
      throw error;
    }
  }

  /**
   * Fetches ICS content for a booking. `icsLink` may be an absolute URL or a path such as
   * `/v1/user/room-bookings/{id}/calendar-links/ics` (resolved against the user API base).
   */
  public async fetchCalendarIcs(icsLink: string): Promise<Blob> {
    const pathOrUrl = this.normalizeUserCalendarIcsTarget(icsLink);
    const response = await this.axiosInstance.get(pathOrUrl, {
      responseType: 'blob',
      headers: {
        Accept: 'text/calendar, application/octet-stream, */*',
      },
    });
    return response.data;
  }

  private normalizeUserCalendarIcsTarget(icsLink: string): string {
    const trimmed = icsLink.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    let path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    const userPrefix = '/v1/user';
    if (path.startsWith(userPrefix)) {
      path = path.slice(userPrefix.length);
    }
    return path.startsWith('/') ? path : `/${path}`;
  }

  public async cancelRoomBooking(roomBookingId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/${roomBookingId}/cancel`);
    } catch (error) {
      console.error(`Error canceling room booking ${roomBookingId}:`, error);
      throw error;
    }
  }

  public async getProfile(): Promise<UserProfile> {
    try {
      const response = await this.axiosInstance.get<UserProfile>('/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  public async updateProfile(request: UpdateProfileRequest): Promise<void> {
    try {
      await this.axiosInstance.put<void>('/profile', request);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  /** POST /v1/user/assets/upload-urls */
  public async generateUploadUrls(request: GenerateUploadUrlsRequest): Promise<GenerateUploadUrlsResponse> {
    try {
      const response = await this.axiosInstance.post<GenerateUploadUrlsResponse>('/assets/upload-urls', request);
      return response.data;
    } catch (error) {
      console.error('Error generating user asset upload URLs:', error);
      throw error;
    }
  }

  public async deleteProfile(withOrg: boolean): Promise<void> {
    try {
      await this.axiosInstance.delete<void>('/profile', {
        params: {
          withOrg,
        },
      });
    } catch (error) {
      console.error('Error deleting user profile:', error);
      throw error;
    }
  }

  // Review-related methods (legacy booking-scoped — prefer space review methods below)
  public async createReview(request: CreateReviewRequest): Promise<void> {
    try {
      await this.axiosInstance.post<void>('/reviews', request);
    } catch (error) {
      console.error('Error creating review:', error);
      throw error;
    }
  }

  public async getReview(reviewId: string): Promise<Review> {
    try {
      const response = await this.axiosInstance.get<Review>(`/reviews/${reviewId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching review ${reviewId}:`, error);
      throw error;
    }
  }

  public async updateReview(reviewId: string, request: UpdateReviewRequest): Promise<Review> {
    try {
      const response = await this.axiosInstance.put<Review>(`/reviews/${reviewId}`, request);
      return response.data;
    } catch (error) {
      console.error(`Error updating review ${reviewId}:`, error);
      throw error;
    }
  }

  /** Preferred: create a space-scoped review (no booking required). */
  public async createSpaceReview(
    spaceId: string,
    request: CreateSpaceReviewRequest,
  ): Promise<SpaceReviewResponse> {
    try {
      const response = await this.axiosInstance.post<SpaceReviewResponse>(
        `/spaces/${spaceId}/reviews`,
        request,
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating space review for ${spaceId}:`, error);
      throw error;
    }
  }

  /**
   * Preferred: fetch the authenticated user's review for a space.
   * Returns null when the user has not reviewed the space (404).
   */
  public async getSpaceReview(spaceId: string): Promise<SpaceReviewResponse | null> {
    try {
      const response = await this.axiosInstance.get<SpaceReviewResponse>(
        `/spaces/${spaceId}/reviews`,
      );
      return response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      console.error(`Error fetching space review for ${spaceId}:`, error);
      throw error;
    }
  }

  public async createTutorReview(
    request: CreateTutorReviewRequest,
  ): Promise<CreateTutorReviewResponse> {
    try {
      const response = await this.axiosInstance.post<CreateTutorReviewResponse>(
        '/tutor-reviews',
        request,
      );
      return response.data;
    } catch (error) {
      console.error('Error creating tutor review:', error);
      throw error;
    }
  }

  /** GET /v1/user/tutor-reviews/{tutorId}/eligibility */
  public async getTutorReviewEligibility(
    tutorId: string,
  ): Promise<TutorReviewEligibilityResponse> {
    try {
      const response = await this.axiosInstance.get<TutorReviewEligibilityResponse>(
        `/tutor-reviews/${tutorId}/eligibility`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching tutor review eligibility for ${tutorId}:`, error);
      throw error;
    }
  }

  /**
   * When `recurringSeriesId` is set, accepts pending change proposals for the whole series via
   * POST /user/series/{recurringSeriesId}/accept. Otherwise accepts a single booking's change.
   */
  public async acceptRoomBookingChange(
    roomBookingId: string,
    changeId: string,
    recurringSeriesId?: string | null,
  ): Promise<void> {
    try {
      if (recurringSeriesId) {
        await this.axiosInstance.post<void>(`/series/${recurringSeriesId}/accept`);
      } else {
        await this.axiosInstance.post<void>(`/room-bookings/${roomBookingId}/changes/${changeId}/accept`);
      }
    } catch (error) {
      console.error(`Error accepting change ${changeId} for room booking ${roomBookingId}:`, error);
      throw error;
    }
  }

  /**
   * When `recurringSeriesId` is set, declines pending change proposals for the whole series via
   * POST /user/series/{recurringSeriesId}/decline. Otherwise declines a single booking's change.
   */
  public async declineRoomBookingChange(
    roomBookingId: string,
    changeId: string,
    request: DeclineChangeRequest,
    recurringSeriesId?: string | null,
  ): Promise<void> {
    try {
      if (recurringSeriesId) {
        await this.axiosInstance.post<void>(`/series/${recurringSeriesId}/decline`, request);
      } else {
        await this.axiosInstance.post<void>(`/room-bookings/${roomBookingId}/changes/${changeId}/decline`, request);
      }
    } catch (error) {
      console.error(`Error declining change ${changeId} for room booking ${roomBookingId}:`, error);
      throw error;
    }
  }

  public async getRewards(): Promise<RewardsSummary> {
    try {
      const response = await this.axiosInstance.get<RewardsSummary>('/rewards');
      return response.data;
    } catch (error) {
      console.error('Error fetching rewards:', error);
      throw error;
    }
  }

  public async redeemVoucher(request: VoucherRedeemRequest): Promise<void> {
    try {
      await this.axiosInstance.post<void>('/rewards/vouchers/redeem', request);
    } catch (error) {
      console.error('Error redeeming voucher:', error);
      throw error;
    }
  }

  /** POST /v1/user/passes/checkout — starts Stripe (ONLINE) or cash pending purchase. */
  public async checkoutPass(request: PassCheckoutRequest): Promise<PassCheckoutResponse> {
    try {
      const response = await this.axiosInstance.post<PassCheckoutResponse>('/passes/checkout', request);
      return response.data;
    } catch (error) {
      console.error('Error checking out pass:', error);
      throw error;
    }
  }

  /** GET /v1/user/passes — wallet list, optionally filtered by target type. */
  public async listWalletPasses(params?: { targetType?: PassTargetType }): Promise<PassWalletSummary[]> {
    try {
      const response = await this.axiosInstance.get<PassWalletSummary[]>('/passes', {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching wallet passes:', error);
      throw error;
    }
  }

  /** GET /v1/user/passes/{passId} */
  public async getWalletPass(passId: string): Promise<PassWalletDetail> {
    try {
      const response = await this.axiosInstance.get<PassWalletDetail>(`/passes/${passId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching wallet pass ${passId}:`, error);
      throw error;
    }
  }

  /** GET /v1/user/passes/{passId}/events */
  public async getWalletPassEvents(passId: string): Promise<PassUsageEvent[]> {
    try {
      const response = await this.axiosInstance.get<PassUsageEvent[]>(`/passes/${passId}/events`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching wallet pass events for ${passId}:`, error);
      throw error;
    }
  }
}

export default UserApiClient;

export const userApiClient = new UserApiClient(); 